import { useCallback, useEffect, useRef, useState } from "react";
import { holdMic, stopStream } from "../lib/micGuard.js";
import { useInterimTranscript } from "./useInterimTranscript.js";

/**
 * useVoiceTurn — the conversation loop behind the voice pill (Plan F).
 *
 *   idle → joining → reading → speak → listening → thinking → reading …
 *                                                            ↘ done
 *   denied: the mic was refused; the caller falls back to typing.
 *
 * The hook owns the microphone: the MediaStream, the MediaRecorder that
 * captures the answer, and an AnalyserNode that measures the room. It decides
 * whose turn it is from that level alone — not from the speech recognizer —
 * so Safari (no interim transcript) behaves like Chrome.
 *
 *   QUIET_MS            after the respondent has spoken, this much quiet
 *                       submits the answer
 *   VOICE_ONSET_MS      level above the floor for this long counts as speech
 *                       (a cough or a chair does not start a turn)
 *   NO_VOICE_REPROMPT_MS in "speak", this long with no voice re-reads the
 *                       question once; it never submits nothing
 *   FLOOR_MULT          the floor is the room's own noise × this
 *
 * Tests inject `analyserFactory` (returns { level(): number }) and `now()`.
 */
export const QUIET_MS = 2000;
export const VOICE_ONSET_MS = 300;
export const NO_VOICE_REPROMPT_MS = 12000;
export const FLOOR_MULT = 2.5;
const SAMPLE_MS = 100;
const CALIBRATE_MS = 400;   // the window whose quietest average is the room's noise
const FLOOR_MIN = 0.004, FLOOR_MAX = 0.08;
const MAX_RECORDING_MS = 10 * 60 * 1000;
const DEVICE_KEY = "cg.voice.deviceId";

function rmsAnalyser(stream) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  const src = ctx.createMediaStreamSource(stream);
  const an = ctx.createAnalyser();
  an.fftSize = 1024;
  src.connect(an);
  const buf = new Uint8Array(an.fftSize);
  return {
    level() {
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
      return Math.sqrt(sum / buf.length);
    },
    close() { try { ctx.close(); } catch { /* fine */ } },
  };
}

export function useVoiceTurn({
  question,            // the current question text, or null
  speak, cancelSpeech, speaking, unlock,
  onAnswer,            // (blob) => Promise — upload + submit; the hook goes to "thinking"
  onEnd,               // called after end()
  analyserFactory = rmsAnalyser,
  now = () => Date.now(),
} = {}) {
  const [state, setState] = useState("idle");
  const [level, setLevel] = useState(0);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(() => {
    try { return window.localStorage.getItem(DEVICE_KEY) || ""; } catch { return ""; }
  });
  const interim = useInterimTranscript();

  const stateRef = useRef("idle");
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const floorRef = useRef(null);
  const calibRef = useRef([]);   // last CALIBRATE_MS of samples
  const noiseRef = useRef(null); // quietest window average seen since the mic opened
  const aboveSinceRef = useRef(null);
  const quietSinceRef = useRef(null);
  const speakSinceRef = useRef(null);
  const repromptedRef = useRef(false);
  const capRef = useRef(null);
  const lastReadRef = useRef(null);
  const releaseRef  = useRef(null);

  function go(next) { stateRef.current = next; setState(next); }

  // ── microphone ────────────────────────────────────────────────────────────
  async function openMic(id) {
    const constraints = { audio: id ? { deviceId: { exact: id } } : true };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;
    analyserRef.current = analyserFactory(stream);
    // Watch the room from the moment the mic opens: the floor is learned
    // while the question is being read, when the respondent is quiet, and
    // only ever moves down after that. Calibrating in the first moments of
    // "speak" instead would mistake a fast answer for the room.
    floorRef.current = null; calibRef.current = []; noiseRef.current = null;
    stopTick();
    tickRef.current = setInterval(tick, SAMPLE_MS);
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter(d => d.kind === "audioinput"));
    } catch { /* no list — the caret hides */ }
    releaseRef.current?.();
    releaseRef.current = holdMic(suspend);
    return stream;
  }
  function closeMic() {
    stopTick();
    stopRecorder(false);
    analyserRef.current?.close?.();
    analyserRef.current = null;
    stopStream(streamRef.current);
    streamRef.current = null;
    releaseRef.current?.(); releaseRef.current = null;
  }
  // The page is not in front (tab backgrounded, phone locked, another app):
  // the mic is released at once and the interview waits. The pill shows the
  // start label again; a tap re-reads the current question. (David, Oct 16.)
  function suspend() {
    const st = stateRef.current;
    if (st === "idle" || st === "done" || st === "denied") return;
    cancelSpeech?.();
    closeMic();
    go("idle");
  }

  // ── recorder ──────────────────────────────────────────────────────────────
  function startRecorder() {
    if (!streamRef.current || !window.MediaRecorder) return;
    const rec = new MediaRecorder(streamRef.current);
    chunksRef.current = [];
    rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorderRef.current = rec;
    rec.start();
    interim.reset();
    interim.start();
    clearTimeout(capRef.current);
    capRef.current = setTimeout(() => submitNow(), MAX_RECORDING_MS);
  }
  function stopRecorder(submit) {
    clearTimeout(capRef.current);
    const rec = recorderRef.current;
    recorderRef.current = null;
    interim.stop();
    if (!rec) return;
    if (submit) {
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        chunksRef.current = [];
        Promise.resolve(onAnswer?.(blob)).catch(() => {});
      };
    }
    try { if (rec.state !== "inactive") rec.stop(); } catch { /* fine */ }
  }
  function submitNow() {
    if (stateRef.current !== "listening" && stateRef.current !== "speak") return;
    go("thinking");
    stopRecorder(true);
  }

  // ── level loop ────────────────────────────────────────────────────────────
  function stopTick() { clearInterval(tickRef.current); tickRef.current = null; }
  function startTurn() {
    // A fresh turn: open the recorder and hand the room to the respondent.
    aboveSinceRef.current = null;
    quietSinceRef.current = null;
    speakSinceRef.current = now();
    go("speak");
    startRecorder();
  }
  function tick() {
    const an = analyserRef.current;
    if (!an) return;
    const lv = an.level();
    setLevel(lv);
    const t = now();
    // Rolling calibration: the quietest CALIBRATE_MS window so far is the room.
    calibRef.current.push(lv);
    if (calibRef.current.length > CALIBRATE_MS / SAMPLE_MS) calibRef.current.shift();
    if (calibRef.current.length === CALIBRATE_MS / SAMPLE_MS) {
      const avg = calibRef.current.reduce((a, b) => a + b, 0) / calibRef.current.length;
      if (noiseRef.current === null || avg < noiseRef.current) {
        noiseRef.current = avg;
        floorRef.current = Math.min(FLOOR_MAX, Math.max(FLOOR_MIN, avg * FLOOR_MULT));
      }
    }
    if (floorRef.current === null) return;
    const above = lv > floorRef.current;
    if (stateRef.current === "speak") {
      if (above) {
        if (aboveSinceRef.current === null) aboveSinceRef.current = t;
        if (t - aboveSinceRef.current >= VOICE_ONSET_MS) { go("listening"); quietSinceRef.current = null; }
      } else {
        aboveSinceRef.current = null;
        if (!repromptedRef.current && t - speakSinceRef.current >= NO_VOICE_REPROMPT_MS) {
          repromptedRef.current = true;
          reread();
        }
      }
    } else if (stateRef.current === "listening") {
      if (above) quietSinceRef.current = null;
      else {
        if (quietSinceRef.current === null) quietSinceRef.current = t;
        if (t - quietSinceRef.current >= QUIET_MS) submitNow();
      }
    }
  }

  // ── reading ───────────────────────────────────────────────────────────────
  function read(text) {
    lastReadRef.current = text;
    go("reading");
    speak?.(text);
  }
  function reread() {
    // Keep the recorder running; re-read over it, then return to "speak".
    go("reading");
    speak?.(lastReadRef.current);
  }
  // When the question has finished being read, it is the respondent's turn.
  useEffect(() => {
    if (stateRef.current !== "reading" || speaking) return;
    if (recorderRef.current) {
      // A re-read: the turn is already open, resume watching without recalibrating.
      speakSinceRef.current = now();
      go("speak");
    } else {
      repromptedRef.current = false;
      startTurn();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speaking, state]);

  // A new question while thinking (or right after joining) → read it.
  useEffect(() => {
    const st = stateRef.current;
    if (!question) return;
    if (st === "thinking" || st === "joining") read(question);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  // ── public actions ────────────────────────────────────────────────────────
  const start = useCallback(async () => {
    if (stateRef.current !== "idle" && stateRef.current !== "denied") return;
    unlock?.();                    // inside the tap: bless TTS audio for iOS
    go("joining");
    try { await openMic(deviceId); }
    catch { closeMic(); go("denied"); return; }
    if (question) read(question);  // else the question effect reads it when it arrives
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, deviceId]);

  const tapPill = useCallback(() => {
    const st = stateRef.current;
    if (st === "listening") submitNow();
    else if (st === "speak") { go("listening"); quietSinceRef.current = null; }
    else if (st === "reading") { cancelSpeech?.(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const end = useCallback(() => {
    cancelSpeech?.();
    closeMic();
    go("done");
    onEnd?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The answer could not be used (transcription failed, nothing heard): hand
  // the turn back by reading the same question again.
  const retry = useCallback(() => {
    if (stateRef.current !== "thinking") return;
    repromptedRef.current = false;
    read(lastReadRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = useCallback(() => {
    // Everything answered: close the mic, say so in the pill.
    closeMic();
    go("done");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDevice = useCallback(async (id) => {
    setDeviceId(id);
    try { window.localStorage.setItem(DEVICE_KEY, id); } catch { /* fine */ }
    if (!streamRef.current) return;
    const st = stateRef.current;
    closeMic();
    try { await openMic(id); } catch { go("denied"); return; }
    if (st === "speak" || st === "listening") startTurn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => { stopTick(); clearTimeout(capRef.current); closeMic(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return {
    state, level, start, tapPill, end, finish, retry,
    devices, deviceId, setDevice,
    interim: interim.interim, interimSupported: interim.supported,
  };
}
